import { useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import type { BlogSummary } from "./blogIndex";

interface BlogArticleProps {
  post: BlogSummary;
  children: ReactNode;
}

export function BlogArticle({ post, children }: BlogArticleProps) {
  return (
    <>
      <section className="relative w-full h-[380px] md:h-[480px] overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stem-dark/40 via-stem-dark/25 to-stem-dark/65" />
        <div className="absolute inset-0 flex flex-col justify-end pb-10 md:pb-14">
          <div className="section-padding max-w-6xl mx-auto w-full">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium mb-4 md:mb-6 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Back to Stories
            </button>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-semibold uppercase tracking-wider">
                {post.category}
              </span>
              <span className="text-white/60 text-xs">{post.readTime}</span>
              <span className="text-white/60 text-xs">{post.date}</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight">
              {post.title}
            </h1>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-stem-cream">
        <article className="section-padding max-w-6xl mx-auto">
          <div className="prose-custom space-y-6 mb-14 md:mb-20 [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-bold [&_h2]:text-stem-dark [&_h2]:tracking-tight [&_p]:text-stem-charcoal [&_p]:text-base [&_p]:md:text-lg [&_p]:leading-relaxed [&_ul]:space-y-3 [&_li]:text-stem-charcoal [&_li]:text-base [&_li]:md:text-lg [&_li]:leading-relaxed">
            {children}
          </div>

          <div className="text-center">
            <BackToStoriesButton />
          </div>
        </article>
      </section>
    </>
  );
}

function BackToStoriesButton() {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate("/blog")}
      className="btn-dark text-sm whitespace-nowrap"
    >
      <i className="ri-arrow-left-line" />
      Back to All Stories
    </button>
  );
}

export function BlogTakeaways({ items }: { items: ReactNode[] }) {
  return (
    <>
      <div className="border-t border-stem-sand" />
      <div className="bg-white rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-revelBlue flex items-center justify-center">
            <i className="ri-lightbulb-line text-white text-lg" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-stem-dark">
            Key Takeaways
          </h3>
        </div>
        <ul className="space-y-3">
          {items.map((item, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-revelBlue/10 flex items-center justify-center mt-0.5">
                <i className="ri-check-line text-revelBlue text-xs" />
              </div>
              <span className="text-stem-charcoal text-sm md:text-base">
                {item}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

export function BlogInlineImage({
  src,
  alt,
  caption,
  maxWidth,
  fit = "cover",
}: {
  src: string;
  alt: string;
  caption?: string;
  maxWidth?: string;
  fit?: "cover" | "contain";
}) {
  return (
    <figure
      className={`overflow-hidden rounded-2xl bg-white border border-stem-sand ${
        maxWidth ? maxWidth : ""
      } mx-auto`}
    >
      <img
        src={src}
        alt={alt}
        className={
          fit === "contain"
            ? "w-full h-auto object-contain"
            : "w-full aspect-[16/9] object-cover object-center"
        }
      />
      {caption && (
        <figcaption className="px-4 py-3 text-sm text-stem-gray leading-relaxed">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
